import React, { Fragment, useCallback, useMemo, useState } from 'react'
import { Box, Stack, Typography } from '@mui/material'
import { FeatureFlag, useFeatureFlag } from 'utils/featureFlags'

import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import { ChangeEmailPopup } from '../ChangeEmailPopup'
import { ClientType } from 'models/user'
import { Country } from 'constants/country'
import { GRAY_900 } from 'constants/styling/theme'
import { KeyboardEventKey } from 'constants/misc'
import { MUIButton } from 'components/common/MUIButton'
import { MUIInputField } from 'components/common/MUIInputField'
import { PreferredPaymentMethodRedesign, useOffersPreferredPaymentMethodChoice } from '../PreferredPaymentMethodSelect'
import { ReceiptEmail } from '../ReceiptEmail'
import { STRIPE_PRIMARY_PRODUCT_KINDS } from 'constants/payments'
import { VATNumberPopup } from '../VATNumberPopup'
import { forgotPasswordPath } from 'components/pages/General/ForgotPassword'
import { useAuth0 } from 'utils/auth'
import { useAvailablePaymentMethods } from '../_main/contexts'
import { useNavigate } from 'react-router-dom'
import { useResetPassword } from 'dataQueries'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useTranslation } from 'react-i18next'
import { useUserData } from 'components/contexts/UserDataContext'

/**
 * @component User Personal information displayed in Profile page
 * @example
 * <PersonalInformation userData={userMeRequest.data} />
 */
export const PersonalInformation: React.FC = () => {
  const { t } = useTranslation(['profile', 'country'])
  const { user } = useAuth0()
  const { organizationData, baseUserData, clientData } = useUserData()
  const { spawnSuccessToast } = useSnackbar()
  const resetPassword = useResetPassword()
  const navigate = useNavigate()
  const isForgotPasswordPageEnabled = useFeatureFlag(FeatureFlag.PUBLIC_FORGOT_PASSWORD_PAGE)

  const [isVATNumberPopupOpen, setIsVATNumberPopupOpen] = useState<boolean>(false)
  const [changeEmailPopup, setChangeEmailPopup] = useState<boolean>(false)
  const { availablePaymentMethods } = useAvailablePaymentMethods()

  const userHasStripePaymentMethod = useMemo(() => availablePaymentMethods.some((method) => STRIPE_PRIMARY_PRODUCT_KINDS.has(method)), [availablePaymentMethods])
  const offersPreferredPaymentMethod = useOffersPreferredPaymentMethodChoice(availablePaymentMethods, clientData?.preferredPaymentMethod)

  const handleRestPassword = useCallback(() => {
    if (isForgotPasswordPageEnabled) {
      navigate(forgotPasswordPath(baseUserData?.email ?? user?.email))
      return
    }
    resetPassword.mutate({}, {
      onSuccess: () => {
        spawnSuccessToast(t('changepassword.success'))
      },
    })
  }, [isForgotPasswordPageEnabled, navigate, baseUserData?.email, user?.email, resetPassword, spawnSuccessToast, t])

  if (!user) return <Fragment></Fragment>

  return (
    <Fragment>

      <Stack gap="1.6rem">
        <Typography variant="text-xl" fontWeight={600} color={GRAY_900}>
          {t('personal_information')}
        </Typography>

        <BorderBoxWrapper padding={2.4} gap={1.6} display="inline-grid" className="fs-exclude">

          <Stack gap={1.6} flexDirection="row" alignItems="center" justifyContent="space-between">

            <Stack width="100%">
              <MUIInputField
                readOnly
                value={baseUserData?.name || ''}
                label={t('name.label')}
              />
            </Stack>

            <Stack width="100%">
              <MUIInputField
                readOnly
                value={organizationData?.name || ''}
                label={t('organization_name.label')}
              />
            </Stack>

          </Stack>

          <Stack gap={1.6} flexDirection="row" alignItems="center" justifyContent="space-between">

            <Stack width="100%">
              <MUIInputField
                readOnly
                label={t('phone.label')}
                prefix={baseUserData?.country || Country.CH}
                value={baseUserData?.phone || ''}
                placeholder={t('phone.placeholder')}
              />
            </Stack>

            <Stack width="100%">
              <MUIInputField
                readOnly
                placeholder={t('country.placeholder')}
                value={(t(`country:${(baseUserData?.country || '').toUpperCase()}`) || '').toString()}
                label={t('country.label')}
              />
            </Stack>

          </Stack>

          <Stack gap={1.6} flexDirection="row" alignItems="center" justifyContent="space-between">

            <Stack width="100%" gap={1.2}>
              <MUIInputField
                readOnly
                value={user.email || ''}
                label={t('email.label')}
              />

              <MUIButton
                type="secondaryBorder"
                size="sm"
                onClick={() => setChangeEmailPopup(true)}
                sx={{ width: 'fit-content' }}
              >
                {t('email.change_email_button')}
              </MUIButton>
            </Stack>

            <Stack width="100%" gap={1.2}>
              <MUIInputField
                readOnly
                value={'********'}
                label={t('password.label')}
              />

              <MUIButton
                type="secondaryBorder"
                size="sm"
                isLoading={resetPassword.isPending}
                disabled={resetPassword.isPending}
                onClick={() => handleRestPassword()}
                sx={{ width: 'fit-content' }}
              >
                {t('password.change_password_button')}
              </MUIButton>
            </Stack>

          </Stack>

          {!!clientData && clientData.clientType === ClientType.B2C &&
            <Stack gap={1.6} width="50%" alignItems="flex-start" justifyContent="space-between">
              <Stack width="100%" gap={1.2}>

                <MUIInputField
                  readOnly
                  value={clientData.vatNumber}
                  label={t('vat_number.label')}
                  placeholder={t('vat_number.placeholder')}
                  errorText={t('vat_number.text')}
                  isError={!clientData.vatNumber}
                />

                {!clientData.vatNumber &&
                  <MUIButton
                    type="secondaryBorder"
                    size="sm"
                    onClick={e => setIsVATNumberPopupOpen(true)}
                    sx={{ width: 'fit-content' }}
                  >
                    {t('vat_number.add')}
                  </MUIButton>
                }

              </Stack>
            </Stack>
          }

          {/* A grid keeps either field at half width when the other one is not shown. */}
          <Box display="grid" gridTemplateColumns="repeat(2, minmax(0, 1fr))" gap={1.6} alignItems="flex-start">

            {userHasStripePaymentMethod &&
              <ReceiptEmail />
            }

            {offersPreferredPaymentMethod &&
              <PreferredPaymentMethodRedesign />
            }

          </Box>

        </BorderBoxWrapper>
      </Stack>

      <ChangeEmailPopup
        isOpen={changeEmailPopup}
        onClickOutside={() => setChangeEmailPopup(false)}
        onClickClose={() => setChangeEmailPopup(false)}
        onKeyDown={(e) => e.key === KeyboardEventKey.ESCAPE && setChangeEmailPopup(false)}
      />

      <VATNumberPopup
        isOpen={isVATNumberPopupOpen === true}
        onClickOutside={() => setIsVATNumberPopupOpen(false)}
        onClickClose={() => setIsVATNumberPopupOpen(false)}
        callbackAfterSuccessfulResponse={() => setIsVATNumberPopupOpen(false)}
      />

    </Fragment>
  )
}
